// A page left open across a deploy asks for code that no longer exists. Reload once and carry on.
//
// ⚠⚠ SEEN LIVE (2026-09-13): a player's map page was opened, three deploys went out, then "Sheet" was clicked — the sheet's
// code file from the old build was gone. Netlify answers every unknown path with index.html (the SPA redirect), so the
// import failed as a MIME error, React had nothing to render, and the whole page went blank. A GM who ships mid-session,
// and a table screen left on all evening, will both hit this.
//
// ⚠ ONCE. The guard stops a genuinely broken file from reloading forever: a second failure inside a minute is a real
// error and is left to show itself.
const KEY = "compendium.staleBuildReload";
const WINDOW_MS = 60_000;

export function reloadForNewBuild(): boolean {
  try {
    const last = Number(sessionStorage.getItem(KEY) ?? 0);
    if (Date.now() - last < WINDOW_MS) return false;
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    return false; // No storage, no guard — so no automatic reload either.
  }
  window.location.reload();
  return true;
}

/** Vite fires this when a lazily imported file can't be fetched (vite.dev/guide/build#load-error-handling). */
export function watchForNewBuild(): void {
  window.addEventListener("vite:preloadError", (event) => {
    if (reloadForNewBuild()) event.preventDefault();
  });
  void pollForNewBuild();
}

/**
 * ⚠ SEEN LIVE (2026-09-14): the owner's page had been open across four deploys; a button shipped hours earlier "wasn't
 * there". Nothing broke, so nothing reloaded. Now the page asks for its own index.html every few minutes and, when
 * the bundle it names has changed, says so (`NEW_BUILD_EVENT`) — a "Reload" the person chooses, never a reload that
 * drops a drag mid-session.
 */
export const NEW_BUILD_EVENT = "compendium:new-build";
const POLL_MS = 3 * 60_000;
const bundleOf = (html: string) => html.match(/src="([^"]*\/assets\/index-[^"]+\.js)"/)?.[1] ?? null;

async function currentBundle(): Promise<string | null> {
  try {
    const r = await fetch(`/index.html?t=${Date.now()}`, { cache: "no-store" });
    return r.ok ? bundleOf(await r.text()) : null;
  } catch { return null; }
}

async function pollForNewBuild(): Promise<void> {
  const mine = [...document.scripts].map((s) => s.getAttribute("src") ?? "").find((s) => /\/assets\/index-.*\.js$/.test(s)) ?? null;
  if (!mine) return;   // dev server, or a harness: nothing to compare
  const tick = async () => {
    if (document.visibilityState !== "visible") return;
    const live = await currentBundle();
    if (live && !mine.endsWith(live.replace(/^\//, "")) && live !== mine) {
      window.dispatchEvent(new CustomEvent(NEW_BUILD_EVENT));
      clearInterval(timer);
    }
  };
  const timer = setInterval(() => void tick(), POLL_MS);
  document.addEventListener("visibilitychange", () => void tick());
}
